import { useClerk } from "@clerk/tanstack-react-start";
import { createFileRoute } from "@tanstack/react-router";

/**
 * ClawInstall admin logout page
 *
 * Signs the admin out of Clerk and lands them, signed out, on the admin door
 * (`/admin/login`) rather than the customer `/login`. Reachable at
 * `clawinstall.ai/admin/logout`; the topbar account menu links here. The file
 * lives under `admin_/` (a TanStack non-nested route), like `admin_/login`, so
 * it sits outside the role-gated `/clawinstall/admin` layout. Signing out an
 * already-signed-out Clerk instance is a harmless no-op redirect.
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/admin_/logout/")({
  component: ClawInstallAdminLogout,
});

/**
 * Module-scoped one-shot guard (mirrors the customer `logoutState`) so we call
 * `signOut` exactly once per Clerk instance without `useEffect` (forbidden in
 * `@fm/web`). Scheduled via `queueMicrotask` so nothing mutates React/atom
 * state during render.
 *
 * Exported only so a unit test can reset it between cases.
 *
 * @since 1.0.0
 */
export const adminLogoutState = { signedOutFrom: null as unknown };

export function ClawInstallAdminLogout() {
  const clerk = useClerk();
  if (clerk !== adminLogoutState.signedOutFrom) {
    adminLogoutState.signedOutFrom = clerk;
    queueMicrotask(() => {
      // Clerk performs the post-sign-out browser navigation itself.
      // `/admin/login` is the public admin door (the domain router maps it to
      // `/clawinstall/admin/login`), matching the admin login's `<SignIn path>`.
      void clerk.signOut({ redirectUrl: "/admin/login" });
    });
  }
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-6">
      <p className="text-on-surface">Signing you out...</p>
    </div>
  );
}
